import { Button, Icon, ImgIcons, ImgThemeConsumer } from 'imaginarity-react-ui';
import * as React from 'react';
import { useImgI18N } from 'services/ImgI18N';
import { InfoBoxSC as T } from './InfoBoxSC';

export type InfoBoxTypes = "warning" | "alert" | "success" | "info" | "default";

interface InfoBoxPublicBaseProps {
    content: string;
    type?: InfoBoxTypes;
    icon?: ImgIcons;
    closeable?: boolean;
    darkMode?: boolean;
    noBold?: boolean;
    noUppercase?: boolean;
    noLetterSpacing?: boolean;
    noBorder?: boolean;
    children?: JSX.Element;
    toggleAble?: boolean;
    animated?: boolean;
    initialOpen?: boolean;
    onClick?: () => void;
    additionalButton?: JSX.Element;
}

export type InfoBoxProps = InfoBoxPublicBaseProps & (
    | { selfCloseInSeconds: number; countDown?: boolean; pauseOnMouseOver?: boolean }
    | { selfCloseInSeconds?: never; countDown?: never; pauseOnMouseOver?: never }
);

const InfoBox = (p: InfoBoxProps) => {
    const { t } = useImgI18N("general");
    const { onClick, animated, countDown, selfCloseInSeconds, pauseOnMouseOver } = p;
    const darkMode = p.darkMode ?? false;
    const withIcon = p.icon !== undefined;

    const [hide, setHide] = React.useState(false);
    const [visible, setVisible] = React.useState(p.initialOpen ?? true);
    const [closing, setClosing] = React.useState(false);
    const [paused, setPaused] = React.useState(false);
    const [cd, setCd] = React.useState({
        value: selfCloseInSeconds ?? 0,
        prev: (selfCloseInSeconds ?? 0) + 1,
        animating: false,
    });

    const selfCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const close = React.useCallback(() => {
        setHide(true);
        if (onClick) onClick();
    }, [onClick]);

    const startClose = React.useCallback(() => {
        if (animated) {
            setClosing(true);
            setTimeout(() => {
                setHide(true);
                setClosing(false);
                if (onClick) onClick();
            }, 200);
        } else {
            close();
        }
    }, [animated, onClick, close]);

    const startCloseRef = React.useRef(startClose);
    React.useEffect(() => { startCloseRef.current = startClose; }, [startClose]);

    const startCountdownInterval = React.useCallback(() => {
        if (!countDown) return;
        countdownIntervalRef.current = setInterval(() => {
            setCd(prev => ({ value: Math.max(0, prev.value - 1), prev: prev.value, animating: true }));
            setTimeout(() => setCd(prev => ({ ...prev, animating: false })), 300);
        }, 1000);
    }, [countDown]);

    React.useEffect(() => {
        if (selfCloseInSeconds === undefined) return;
        selfCloseTimerRef.current = setTimeout(() => startCloseRef.current(), selfCloseInSeconds * 1000);
        startCountdownInterval();
        return () => {
            if (selfCloseTimerRef.current !== null) clearTimeout(selfCloseTimerRef.current);
            if (countdownIntervalRef.current !== null) clearInterval(countdownIntervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pauseCountdown = React.useCallback(() => {
        if (selfCloseTimerRef.current !== null) { clearTimeout(selfCloseTimerRef.current); selfCloseTimerRef.current = null; }
        if (countdownIntervalRef.current !== null) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
        setPaused(true);
        setCd(prev => ({ ...prev, animating: false }));
    }, []);

    const resumeCountdown = React.useCallback(() => {
        selfCloseTimerRef.current = setTimeout(() => startCloseRef.current(), cd.value * 1000);
        startCountdownInterval();
        setPaused(false);
    }, [cd.value, startCountdownInterval]);

    const color = p.type === "alert" ? "@accentRed"
        : p.type === "warning" ? "@accentYellow"
        : p.type === "success" ? "@accentGreen"
        : p.type === "info" ? "@accentBlue"
        : undefined;

    return (
        <ImgThemeConsumer>
            {(theme) => {
                const resolvedColor = color ?? theme.color;
                return (
                    <T.InfoBoxContainer
                        color={resolvedColor}
                        hide={hide}
                        darkMode={darkMode}
                        icon={withIcon}
                        noBold={p.noBold ?? false}
                        noUppercase={p.noUppercase ?? false}
                        animated={animated ?? false}
                        noBorder={p.noBorder ?? false}
                        noLetterSpacing={p.noLetterSpacing ?? false}
                        closing={closing}
                        onMouseEnter={pauseOnMouseOver ? pauseCountdown : undefined}
                        onMouseLeave={pauseOnMouseOver ? resumeCountdown : undefined}
                    >
                        {p.icon &&
                            <div className="icon">
                                <Icon name={p.icon} marginTop={5} size="22px" />
                            </div>
                        }
                        <div style={{ padding: "0 0 0 10px" }}>
                            {p.closeable &&
                                <Button kind="halfTransparentButton" icon="times" floated="right" onClick={close} tooltip={{ tooltipText: t('click to close') }} />
                            }
                            {p.toggleAble &&
                                <Button kind="halfTransparentButton" icon={visible ? "chevron up" : "chevron down"} floated="right" onClick={() => setVisible(v => !v)} />
                            }
                            {p.content}
                            {p.additionalButton &&
                                <T.InfoBoxContainerBtn color={resolvedColor}>{p.additionalButton}</T.InfoBoxContainerBtn>
                            }
                            {countDown && selfCloseInSeconds !== undefined &&
                                <T.CountdownContainer color={resolvedColor}>
                                    {paused
                                        ? <T.CountdownDigit state="idle"><Icon name="pause" color={resolvedColor} marginTop={0} /></T.CountdownDigit>
                                        : <>
                                            {cd.animating &&
                                                <T.CountdownDigit state="exiting">{cd.prev}</T.CountdownDigit>
                                            }
                                            <T.CountdownDigit state={cd.animating ? "entering" : "idle"}>
                                                {cd.value}
                                            </T.CountdownDigit>
                                        </>
                                    }
                                </T.CountdownContainer>
                            }
                        </div>
                        {p.children && visible &&
                            <div style={{ gridRowStart: 2, gridRowEnd: 3, gridColumnStart: 1, gridColumnEnd: withIcon ? 3 : 2 }}>{p.children}</div>
                        }
                    </T.InfoBoxContainer>
                );
            }}
        </ImgThemeConsumer>
    );
};

export default InfoBox;
